import { DateTime } from 'luxon'
import { z } from 'zod'
import { LuxonDateTime } from '~/shared/common/zod-luxon'
import type { Database } from '~/shared/common/supabase/database.types'

type AgreementVersionRow = Database['public']['Tables']['identity_agreementversion']['Row']
type AgreementConsentRow = Database['public']['Tables']['identity_agreementconsent']['Row']

// Currently-recognized agreement types. The DB column is a free-text varchar.
// Server code uses AgreementType.safeParse(...) to silently drop rows with
// types not in this enum (e.g. retired/renamed types still sitting in the
// table). Add new types here when extending.
export const AgreementType = z.enum(['tos', 'privacy', 'customer_agreement'])
export type AgreementType = z.infer<typeof AgreementType>

// Date-only ISO string (YYYY-MM-DD) → Luxon DateTime. Used for version_date /
// active_date. Mirrors LuxonDateTime but accepts the shorter date form.
const LuxonDateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD date')
  .transform((s, ctx) => {
    const dt = DateTime.fromISO(s, { zone: 'utc' })
    if (!dt.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid date: ${dt.invalidExplanation ?? 'unknown reason'}`,
      })
      return z.NEVER
    }
    return dt
  })

// ---------- AgreementVersion ----------

export const AgreementVersionRowFromSupabase = z.object({
  id: z.number(),
  type: z.string(),
  version_date: z.string().nullable(),
  active_date: z.string().nullable(),
  markdown_content: z.string().nullable(),
}) satisfies z.ZodType<
  Pick<AgreementVersionRow, 'id' | 'type' | 'version_date' | 'active_date' | 'markdown_content'>,
  Pick<AgreementVersionRow, 'id' | 'type' | 'version_date' | 'active_date' | 'markdown_content'>
>

export type AgreementVersionRow_Boundary = z.output<typeof AgreementVersionRowFromSupabase>

// Wire shape — identical to the raw Supabase row pick above. Exported as the
// API contract so the server returns it directly and the client re-parses
// through the same schema to derive the Luxon-typed domain shape.
export const AgreementVersionWire = AgreementVersionRowFromSupabase
export type AgreementVersionWire = z.output<typeof AgreementVersionWire>

// Domain shape — Luxon DateTime for dates, narrowed type.
export const AgreementVersion = z.object({
  id: z.number(),
  type: AgreementType,
  version_date: LuxonDateOnly.nullable(),
  active_date: LuxonDateOnly.nullable(),
  markdown_content: z.string().nullable(),
})
export type AgreementVersion = z.output<typeof AgreementVersion>

export const agreementVersionFromWire = (raw: unknown): AgreementVersion => {
  const row = AgreementVersionWire.parse(raw)
  return AgreementVersion.parse(row)
}

// ---------- AgreementConsent ----------

export const AgreementConsentRowFromSupabase = z.object({
  id: z.number(),
  user_id: z.number(),
  agreement_version_id: z.number(),
  consent_datetime: z.string(),
  consent_url: z.string().nullable(),
}) satisfies z.ZodType<
  Pick<AgreementConsentRow, 'id' | 'user_id' | 'agreement_version_id' | 'consent_datetime' | 'consent_url'>,
  Pick<AgreementConsentRow, 'id' | 'user_id' | 'agreement_version_id' | 'consent_datetime' | 'consent_url'>
>

export const AgreementConsent = z.object({
  id: z.number(),
  user_id: z.number(),
  agreement_version_id: z.number(),
  consent_datetime: LuxonDateTime,
  consent_url: z.string().nullable(),
})
export type AgreementConsent = z.output<typeof AgreementConsent>
